import { ColorPicker, DatePicker, Flex, Form, Input, Skeleton, Space, Switch } from "antd";
import { DefaultButton } from "components/Shared/DefaultButton";
import dayjs from "dayjs";
import { UniformItemBottomTypes } from "enums/uniformItemBottomTypes";
import type { UniformItemColors } from "enums/uniformItemColors";
import { UniformItemTypes } from "enums/uniformItemTypes";
import { useSportConfiguration } from "hooks/useSportConfiguration";
import { useDeleteUniform, useDeleteUniformItem, useFetchUniformItems, useUpsertUniform } from "hooks/useUniformApi";
import { isEmpty } from "lodash";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import type { UniformFormType } from "types/uniformFormType";
import { getColorPickerValue } from "utils/getColorPickerValue";
import { transformUniformItemsResponse } from "../../utils/transformUniformItemsResponse";
import { UniformSet } from "./components/UniformSet";

const StyledContainer = styled.div`
  opacity: 1;
  padding: 10px;
  min-width: 700px;

  gap: 15px;
  display: flex;
  flex-direction: row;
  align-items: start;

  & > :first-child {
    flex-grow: 1;
  }

  > svg {
    display: flex;
    flex: 1;
  }
`;
StyledContainer.displayName = "StyledContainer";

const StyledSwitch = styled(Switch)`
  margin: 5px auto 10px auto;
  display: block;
`;
StyledSwitch.displayName = "StyledSwitch";

type Props = {
  readonly?: boolean;
  entityId: string;
  uniform?: any;
  onAdded?: (uniformId: string) => void;
  onDeleted?: () => void;
};

export const Uniform = ({ entityId, uniform, readonly, onAdded, onDeleted }: Props) => {
  const [form] = Form.useForm();
  const { formatMessage } = useIntl();
  const isEdit = !isEmpty(uniform);
  const { configuration } = useSportConfiguration();
  const {
    data: uniformItemsData,
    isPending: isUniformItemsLoading,
    refetch: refetchUniformItems,
  } = useFetchUniformItems({ entityId, uniformId: uniform?.uniformId });
  const uniformItems = uniformItemsData?.uniformItems;
  const { mutateAsync: upsertUniform, isPending: isUpsertUniformInProgress } = useUpsertUniform();
  const { mutateAsync: deleteUniform, isPending: isDeletingUniformInProgress } = useDeleteUniform();
  const { mutateAsync: deleteUniformItem, isPending: isDeletingUniformItemInProgress } = useDeleteUniformItem();

  const [bottomType, setBottomType] = useState<UniformItemBottomTypes>(
    configuration.uniforms.bottomOptions[0] ?? UniformItemBottomTypes.Shorts
  );

  const initialFormValues = {
    nameLocal: uniform?.nameLocal,
    nameLatin: uniform?.nameLatin,
    dateFrom: isEmpty(uniform?.dateFrom?.split("T")?.[0])
      ? null
      : dayjs(uniform?.dateFrom?.split("T")?.[0], "YYYY-MM-DD"),
    dateTo: isEmpty(uniform?.dateTo?.split("T")?.[0]) ? null : dayjs(uniform?.dateTo?.split("T")?.[0], "YYYY-MM-DD"),
  };

  const onSubmitButtonHandler = useCallback(async () => {
    const values = await form.validateFields();

    if (values.dateFrom) {
      values.dateFrom = values.dateFrom.format("YYYY-MM-DD");
    }
    if (values.dateTo) {
      values.dateTo = values.dateTo.format("YYYY-MM-DD");
    }

    const items = Object.entries((values as UniformFormType).uniformParts)
      .filter(([, uniformPart]) => Object.values(uniformPart.colors).some(Boolean))
      .map((entry) => {
        const itemType = entry[0] as UniformItemTypes;
        const uniformPart = entry[1];
        return {
          itemType,
          nameLatin: uniformPart.nameLatin,
          nameLocal: uniformPart.nameLocal,
          uniformItemId: uniformPart.uniformItemId,
          colors: Object.entries(uniformPart.colors).reduce(
            (acc, entry) => {
              const colorType = entry[0] as UniformItemColors;
              const color = entry[1];
              acc[colorType] = getColorPickerValue(color);
              return acc;
            },
            {} as { [key in UniformItemColors]?: string }
          ),
        };
      });

    const uniformResponse = await upsertUniform({
      uniformId: uniform?.uniformId,
      entityId,
      upsertData: {
        dateFrom: values.dateFrom,
        dateTo: values.dateTo,
        nameLocal: values.nameLocal,
        nameLatin: values.nameLatin,
      },
      items,
    });

    const uniformId = uniformResponse?.data?.[0]?.uniformId;
    if (uniformId && onAdded && !isEdit) {
      onAdded(uniformId);
    }

    refetchUniformItems();
  }, [form, onAdded, refetchUniformItems, entityId, isEdit, uniform?.uniformId, upsertUniform]);

  const onDeleteButtonHandler = useCallback(async () => {
    if (uniform) {
      const deleteItemPromises = (uniformItems ?? []).map((item) =>
        deleteUniformItem({ entityId, uniformItemId: item.uniformItemId })
      );
      await Promise.all(deleteItemPromises);
      await deleteUniform({ entityId, uniformId: uniform.uniformId });
    }
    onDeleted?.();
  }, [deleteUniform, deleteUniformItem, uniformItems, uniform, onDeleted, entityId]);

  useEffect(() => {
    if (!uniformItems) return;
    form.setFieldsValue({ uniformParts: uniformItemsData.transformedParts });
  }, [form, uniformItems, uniformItemsData]);

  const uniformParts = Form.useWatch<UniformFormType["uniformParts"]>(["uniformParts"], form);

  const isMutationDisabled =
    isUniformItemsLoading ||
    isUpsertUniformInProgress ||
    isDeletingUniformInProgress ||
    isDeletingUniformItemInProgress;

  if (isUniformItemsLoading) {
    return <Skeleton />;
  }

  return (
    <StyledContainer>
      <div>
        {configuration.uniforms.bottomOptions.length > 1 && (
          <StyledSwitch
            checkedChildren={formatMessage({ id: "uniform.skirts" })}
            unCheckedChildren={formatMessage({ id: "uniform.shorts" })}
            value={bottomType === UniformItemBottomTypes.Skirts}
            onChange={(value) => setBottomType(value ? UniformItemBottomTypes.Skirts : UniformItemBottomTypes.Shorts)}
          />
        )}
        <Form
          form={form}
          key={uniform?.uniformId}
          labelCol={{ span: 12 }}
          wrapperCol={{ span: 12 }}
          initialValues={initialFormValues}
          style={{ marginTop: 10 }}
          disabled={readonly}
        >
          <Form.Item
            name={"nameLocal"}
            label={formatMessage({ id: "uniformModal.fields.nameLocal" })}
            style={{ marginBottom: "5px" }}
            rules={[{ max: 150, required: true }]}
            hidden={readonly}
          >
            <Input disabled={readonly} />
          </Form.Item>
          <Form.Item
            name={"nameLatin"}
            label={formatMessage({ id: "uniformModal.fields.nameLatin" })}
            style={{ marginBottom: "5px" }}
            rules={[{ max: 150 }]}
            hidden={readonly}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={"dateFrom"}
            label={formatMessage({ id: "uniformModal.fields.dateFrom" })}
            style={{ marginBottom: "5px" }}
            hidden={readonly}
          >
            <DatePicker size={"middle"} format={"DD/MM/YYYY"} />
          </Form.Item>
          <Form.Item
            name={"dateTo"}
            label={formatMessage({ id: "uniformModal.fields.dateTo" })}
            style={{ marginBottom: "5px" }}
            hidden={readonly}
          >
            <DatePicker size={"middle"} format={"DD/MM/YYYY"} />
          </Form.Item>
          {Object.entries(transformUniformItemsResponse(configuration.uniforms.items)).map((entry) => {
            const itemType = entry[0] as UniformItemTypes;
            const itemData = entry[1];

            const isBottom = itemType === UniformItemTypes.Bottom;
            const bottomIntlKey = bottomType === UniformItemBottomTypes.Skirts ? "uniform.skirts" : "uniform.shorts";

            return (
              <Form.Item
                key={itemType}
                style={{ marginBottom: "5px" }}
                label={formatMessage({
                  id: isBottom ? bottomIntlKey : `uniform.${itemType}`,
                })}
              >
                <Flex>
                  {Object.keys(itemData.colors).map((color) => (
                    <Fragment key={color}>
                      <Form.Item<UniformFormType>
                        key={color}
                        style={{ marginBottom: "5px" }}
                        name={["uniformParts", itemType, "colors", color as UniformItemColors]}
                      >
                        <ColorPicker defaultFormat="hex" disabledAlpha />
                      </Form.Item>
                      <Form.Item<UniformFormType>
                        layout="vertical"
                        hidden
                        name={["uniformParts", itemType, "uniformItemId"]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item<UniformFormType>
                        layout="vertical"
                        hidden
                        name={["uniformParts", itemType, "nameLocal"]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item<UniformFormType>
                        layout="vertical"
                        hidden
                        name={["uniformParts", itemType, "nameLatin"]}
                      >
                        <Input />
                      </Form.Item>
                    </Fragment>
                  ))}
                </Flex>
              </Form.Item>
            );
          })}
          {!readonly && (
            <Form.Item wrapperCol={{ offset: 8, span: 16 }} style={{ textAlign: "right" }}>
              <Space>
                <DefaultButton
                  htmlType="submit"
                  loading={isUpsertUniformInProgress}
                  onClick={onSubmitButtonHandler}
                  disabled={isMutationDisabled}
                >
                  {formatMessage({
                    id: isEdit ? "button.update" : "button.add",
                  })}
                </DefaultButton>
                {uniform && (
                  <DefaultButton
                    type="default"
                    loading={isDeletingUniformInProgress || isDeletingUniformItemInProgress}
                    onClick={onDeleteButtonHandler}
                    disabled={isMutationDisabled}
                  >
                    {formatMessage({ id: "button.delete" })}
                  </DefaultButton>
                )}
              </Space>
            </Form.Item>
          )}
        </Form>
      </div>
      <Flex>
        {configuration.uniforms.items.map((uniformConfig) => (
          <UniformSet
            uniformConfig={uniformConfig}
            uniformParts={uniformParts}
            bottomType={bottomType}
            key={`${uniformConfig.titleIntlKey}-${uniformConfig.topType.join("-")}-${Object.keys(
              uniformConfig.itemTypes
            ).join("-")}`}
          />
        ))}
      </Flex>
    </StyledContainer>
  );
};
